import {
  Box,
  EmptyState,
  EmptyStateTitle,
  Icon,
  Link,
  Spinner,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Tooltip,
} from '@customerio/pluma-components/react';
import type { Customer } from '@/services/db/customer/entity';
import { isEmptyString } from '@/utils/string';

/**
 * The results area's state: nothing searched yet, a search in flight, or the
 * matches of the last search.
 */
export type SearchResultsState =
  | { status: 'idle' }
  | { status: 'searching' }
  | { status: 'done'; matches: readonly Customer[] };

type CustomerSearchResultsProps = {
  results: SearchResultsState;
  /** The phone column shows only where phone identifies people. */
  canIdentifyByPhone: boolean;
  /** The workspace's words for people: title-cased plural for the caption, plural for the empty state. */
  peopleTitlePlural: string;
  peoplePlural: string;
  onSelect: (customer: Customer) => void;
};

export function CustomerSearchResults({
  results,
  canIdentifyByPhone,
  peopleTitlePlural,
  peoplePlural,
  onSelect,
}: CustomerSearchResultsProps) {
  switch (results.status) {
    case 'idle':
      return null;
    case 'searching':
      return (
        <Box p="300" textAlign="center">
          <Spinner shouldShowLoadingText label="Just a second…" />
        </Box>
      );
    case 'done': {
      if (results.matches.length === 0) {
        return (
          <EmptyState>
            <EmptyStateTitle icon="person">No {peoplePlural} found</EmptyStateTitle>
          </EmptyState>
        );
      }
      return (
        <Table
          caption={`List of ${peopleTitlePlural} matching your search`}
          layout="fixed"
          data-test="people-merge-customers-table"
        >
          <TableThead>
            <TableTr>
              <TableTh pl="200">Email</TableTh>
              {canIdentifyByPhone ? <TableTh>Phone</TableTh> : null}
              <TableTh>id</TableTh>
              <TableTh pr="200" />
            </TableTr>
          </TableThead>
          <TableTbody>
            {results.matches.map((customer) => {
              const email = customer.attributes.email ?? '';
              const phone = customer.attributes.phone ?? '';
              const id = customer.identifiers?.id ?? '';
              return (
                <TableTr key={customer.id} data-test="people-merge-customers-row" data-customer-id={customer.id}>
                  <TableTd pl="200">
                    <Box truncate className="fs-mask" data-test="people-merge-customers-table-email">
                      {isEmptyString(email) ? 'No email' : email}
                    </Box>
                  </TableTd>
                  {canIdentifyByPhone ? (
                    <TableTd>
                      <Box truncate className="fs-mask" data-test="people-merge-customers-table-phone">
                        {isEmptyString(phone) ? 'No phone' : phone}
                      </Box>
                    </TableTd>
                  ) : null}
                  <TableTd>
                    <Tooltip content={id} unsafe_tooltipClassName="fs-mask">
                      <Box truncate className="fs-mask" data-test="people-merge-customers-table-id">
                        {id}
                      </Box>
                    </Tooltip>
                  </TableTd>
                  <TableTd textAlign="right" pr="200">
                    <Link
                      variant="secondary"
                      whiteSpace="nowrap"
                      onClick={() => onSelect(customer)}
                      data-test="people-merge-customer-select-button"
                    >
                      Select
                      <Icon name="chevron-right" size="sm" display="inline-block" />
                    </Link>
                  </TableTd>
                </TableTr>
              );
            })}
          </TableTbody>
        </Table>
      );
    }
    default: {
      const _exhaustive: never = results;
      return _exhaustive;
    }
  }
}
